import React, { useLayoutEffect, useRef } from 'react'
import moment from 'moment'
import queryString from 'query-string'
import { useLocation, useNavigate } from 'react-router-dom'
import { profileStorage } from '@/services/tokenStorage'

export type ListDefaultQueryConfig = {
  /** Key URL mốc đầu / cuối (`YYYY-MM-DD`), vd. `['tuNgay', 'denNgay']`. */
  time?: { from: string; to: string }
  /** Key URL chi nhánh — gán chi nhánh đang đứng. */
  chiNhanhKey?: string
  /**
   * Sort mặc định theo ngày chứng từ nghiệp vụ (thời gian bán / đặt / chuyển… —
   * ngày có thể chọn ngược), giảm dần.
   */
  sort?: {
    field: string
    dir?: 'asc' | 'desc'
    /** Mặc định `sort` (kiem ke dùng `sortField`). */
    fieldKey?: string
    /** Mặc định `sortDir` (kiem ke dùng `sortOrder`). */
    dirKey?: string
  }
  /** Query cố định thêm (vd. tab). */
  extra?: Record<string, string>
}

export function currentMonthRange(): { from: string; to: string } {
  return {
    from: moment().startOf('month').format('YYYY-MM-DD'),
    to: moment().endOf('month').format('YYYY-MM-DD'),
  }
}

export function currentStandingChiNhanhIdText(): string {
  const profile = profileStorage.getProfile() as { chiNhanh?: { id?: number | string } } | null
  const id = Number(profile?.chiNhanh?.id)
  return Number.isFinite(id) && id > 0 ? String(id) : ''
}

export function buildListDefaultQuery(config: ListDefaultQueryConfig): Record<string, string> {
  const out: Record<string, string> = { ...(config.extra ?? {}) }
  if (config.time) {
    const r = currentMonthRange()
    out[config.time.from] = r.from
    out[config.time.to] = r.to
  }
  if (config.chiNhanhKey) {
    const cn = currentStandingChiNhanhIdText()
    if (cn) out[config.chiNhanhKey] = cn
  }
  if (config.sort?.field) {
    const fieldKey = config.sort.fieldKey ?? 'sort'
    const dirKey = config.sort.dirKey ?? 'sortDir'
    out[fieldKey] = config.sort.field
    out[dirKey] = config.sort.dir ?? 'desc'
  }
  return out
}

type Props = ListDefaultQueryConfig & { children: React.ReactNode }

/**
 * Vào màn list với URL trống → gán mặc định thời gian tháng này + chi nhánh đang đứng
 * rồi mới render list (tránh gọi API 2 lần). URL đã có query (deep link, đã lọc) → giữ nguyên.
 */
const ListDefaultQueryGate: React.FC<Props> = ({ children, ...config }) => {
  const { pathname, search, state } = useLocation()
  const navigate = useNavigate()
  const daRender = useRef(false)
  const defaultQs =
    !search || search === '?' ? queryString.stringify(buildListDefaultQuery(config)) : ''
  const needsDefaults = defaultQs !== ''

  useLayoutEffect(() => {
    if (!needsDefaults) return
    navigate({ pathname, search: `?${defaultQs}` }, { replace: true, state })
  }, [needsDefaults, pathname])

  // Trang đã mount rồi tự bỏ query (vd. xóa `?editId=` / `?action=create` sau khi mở form)
  // → không unmount, nếu không form/chi tiết vừa mở sẽ mất.
  if (needsDefaults && !daRender.current) return null
  daRender.current = true
  return <>{children}</>
}

/** `lazy(import)` cho route list kèm mặc định tháng này + chi nhánh đang đứng. */
export function lazyWithListDefaults(
  loader: () => Promise<{ default: React.ComponentType }>,
  config: ListDefaultQueryConfig,
): React.LazyExoticComponent<React.ComponentType> {
  return React.lazy(() =>
    loader().then(({ default: Page }) => {
      const Wrapped: React.FC = () => (
        <ListDefaultQueryGate {...config}>
          <Page />
        </ListDefaultQueryGate>
      )
      return { default: Wrapped }
    }),
  )
}

export default ListDefaultQueryGate
